/* Hook-free: the portal renders the same three legs from its own source. */

import { useTranslation } from "react-i18next";
import "../../../../i18n";
import { AckLeg, Notice } from "../../../../design/components";
import { dateTime, relative } from "../../../../lib/format";
import type { AttemptItem, LegRef, OutcomeLegs } from "../../../../api/types";
import styles from "../DocumentInspector.module.css";

// Only the leg-shaped fields: `attempts` is a list, not a `LegRef`, so it stays out of
// this union rather than widening `legs?.[key]`'s inferred type below.
type LegKey = "aspAck" | "peppolMls" | "otaRef";

function legState(leg: LegRef | null | undefined): "ok" | "pending" | "failed" | "na" {
  if (leg?.status === "failed") return "failed";
  // Not applicable is neither a pass nor a wait: another party owns the leg.
  if (leg?.status === "not_applicable") return "na";
  return leg?.status === "ok" ? "ok" : "pending";
}

function legBody(leg: LegRef | null | undefined, fallback: string, notApplicable: string, notApplicableWithMessage: (message: string) => string): string {
  if (leg?.status === "not_applicable") {
    return leg.message ? notApplicableWithMessage(leg.message) : notApplicable;
  }
  return leg?.message ?? fallback;
}

const ATTEMPT_RESULT_LABEL: Record<string, string> = {
  accepted: "Accepted",
  duplicate: "Already held by the service provider — not sent again",
  rejected: "Rejected",
  unknown: "No answer from the service provider — sent again",
};

/** HUB-I05: the hub's own proof that this document was posted at most once per attempt. */
function AttemptsSection({ attempts }: { attempts: AttemptItem[] }) {
  if (attempts.length === 0) return null;
  return (
    <div className="card-body">
      <h3>Send attempts</h3>
      {attempts.map((attempt, index) => (
        <p key={index} className={styles.sub}>
          <span className="mono">{dateTime(attempt.startedAt)}</span>
          {" — "}
          {attempt.finishedAt
            ? (ATTEMPT_RESULT_LABEL[attempt.result ?? ""] ?? attempt.result ?? "unknown")
            : "being processed by " + (attempt.replicaId ?? "a worker")}
          {attempt.finishedAt && attempt.replicaId ? ` (${attempt.replicaId})` : ""}
        </p>
      ))}
    </div>
  );
}

/** Everything below the card head, so the portal can feed it from its own source. */
export function OutcomeBody({ legs, isError }: { legs: OutcomeLegs | undefined; isError: boolean }) {
  const { t } = useTranslation();

  const LEGS: { key: LegKey; leg: string; name: string; body: string }[] = [
    {
      key: "aspAck",
      leg: t("documents.inspector.outcome.legs.aspAck.leg", "Service provider → Hub"),
      name: t("documents.inspector.outcome.legs.aspAck.name", "Validated and accepted by the ASP"),
      body: t(
        "documents.inspector.outcome.legs.aspAck.body",
        "The service provider confirms it holds the e-invoice and accepted it. This is custody, not delivery.",
      ),
    },
    {
      key: "peppolMls",
      leg: t("documents.inspector.outcome.legs.peppolMls.leg", "Peppol network → Hub"),
      name: t("documents.inspector.outcome.legs.peppolMls.name", "Delivered to the recipient's access point"),
      body: t(
        "documents.inspector.outcome.legs.peppolMls.body",
        "The recipient's access point confirmed receipt. This leg is outside the group's control and can take hours.",
      ),
    },
    {
      key: "otaRef",
      leg: t("documents.inspector.outcome.legs.otaRef.leg", "Tax authority → Service provider → Hub"),
      name: t("documents.inspector.outcome.legs.otaRef.name", "Reported to the Oman Tax Authority"),
      body: t(
        "documents.inspector.outcome.legs.otaRef.body",
        "The service provider validates and reports to the Oman Tax Authority, and passes the acknowledgement back.",
      ),
    },
  ];

  const notApplicable = t("documents.inspector.outcome.notApplicable", "Not applicable");
  const notApplicableWithMessage = (message: string) =>
    t("documents.inspector.outcome.notApplicableWithMessage", "Not applicable — {{message}}", { message });

  return (
    <>
      <div className="card-body">
        <Notice kind="info">
          {t(
            "documents.inspector.outcome.explainer",
            "Three acknowledgements arrive on three separate legs, at three different times. The hub tracks each leg on its own, which is why one synchronous response is not enough.",
          )}
        </Notice>
      </div>

      <div>
        {LEGS.map(({ key, leg, name, body }) => {
          const value = legs?.[key] ?? null;
          return (
            <AckLeg
              key={key}
              leg={leg}
              name={name}
              body={legBody(value, body, notApplicable, notApplicableWithMessage)}
              state={legState(value)}
              {...(value?.reference ? { ref: value.reference } : {})}
              {...(value?.at ? { at: value.at, elapsed: relative(value.at) } : {})}
            />
          );
        })}
      </div>

      {isError ? (
        <div className="card-body">
          <Notice kind="fail">
            {t("documents.inspector.outcome.loadError", "The acknowledgements could not be loaded.")}
          </Notice>
        </div>
      ) : null}

      <AttemptsSection attempts={legs?.attempts ?? []} />

      <div className="card-body">
        <p className={styles.sub}>
          {t(
            "documents.inspector.outcome.awaitingNote",
            'A leg that has not arrived reads “awaiting”. The service provider pushes each acknowledgement as it lands.',
          )}
        </p>
      </div>
    </>
  );
}

